import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicePage.css';
import { setPageMeta } from '../utils/seo';

const HRPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      'HR Policy Management | HRCONSOL',
      'Legally compliant HR policies, employee handbooks, and governance frameworks tailored to your organization.'
    );
  }, []);

  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="container">
          <h1>HR Policy Management – Building Fair and Compliant Workplaces</h1>
          <p className="subtitle">HRCONSOL designs and implements comprehensive HR policies that ensure compliance, fairness, consistency, and employee engagement across your organization.</p>
        </div>
      </div>

      <div className="container">
        <div className="service-content">
          <div className="service-section">
            <p>Well-defined HR policies are the backbone of any successful organization. They provide clarity, protect both employees and employers, and create a culture of trust and accountability. HRCONSOL's <strong>HR Policy Management Services</strong> help businesses develop, document, and implement policies that align with legal requirements and organizational values.</p>
            <p>From employee handbooks to code of conduct, we ensure your policies are clear, comprehensive, and compliant.</p>
          </div>

          <div className="service-section">
            <h2>Scope of Services</h2>
            <p>Our HR Policy Solutions Include:</p>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-book"></i> Employee Handbook Development</h3>
                <p>Comprehensive handbooks covering all HR policies and procedures.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-gavel"></i> Labour Law Compliance</h3>
                <p>Ensuring policies align with Pakistan labor laws and regulations.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-clock"></i> Attendance & Leave Policies</h3>
                <p>Leave management, time-off policies, and attendance tracking guidelines.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-money-bill-wave"></i> Compensation & Benefits</h3>
                <p>Salary structures, bonuses, allowances, and benefits policies.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-shield-alt"></i> Code of Conduct</h3>
                <p>Ethical guidelines, workplace behavior, and disciplinary procedures.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-user-shield"></i> Anti-Harassment Policies</h3>
                <p>Workplace safety, harassment prevention, and grievance mechanisms.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-user-plus"></i> Recruitment & Onboarding</h3>
                <p>Hiring policies, background checks, and new employee orientation.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-chart-line"></i> Performance & Termination</h3>
                <p>Performance review policies, exit procedures, and termination protocols.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Policy Areas We Cover</h2>
            <ul className="benefits-list">
              <li><i className="fas fa-check"></i> Recruitment & Selection Policy</li>
              <li><i className="fas fa-check"></i> Employee Code of Conduct</li>
              <li><i className="fas fa-check"></i> Attendance, Leave & Time-Off Policy</li>
              <li><i className="fas fa-check"></i> Compensation & Benefits Policy</li>
              <li><i className="fas fa-check"></i> Performance Management Policy</li>
              <li><i className="fas fa-check"></i> Training & Development Policy</li>
              <li><i className="fas fa-check"></i> Health & Safety Policy</li>
              <li><i className="fas fa-check"></i> Anti-Harassment & Discrimination Policy</li>
              <li><i className="fas fa-check"></i> Grievance & Complaint Mechanism</li>
              <li><i className="fas fa-check"></i> Data Privacy & Confidentiality Policy</li>
              <li><i className="fas fa-check"></i> Social Media & IT Usage Policy</li>
              <li><i className="fas fa-check"></i> Termination & Exit Policy</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Industries We Serve</h2>
            <ul className="benefits-list">
              <li><i className="fas fa-laptop-code"></i> IT & Technology</li>
              <li><i className="fas fa-industry"></i> Manufacturing & Production</li>
              <li><i className="fas fa-university"></i> Banking & Financial Services</li>
              <li><i className="fas fa-shopping-cart"></i> Retail & E-Commerce</li>
              <li><i className="fas fa-pills"></i> Healthcare & Pharmaceuticals</li>
              <li><i className="fas fa-graduation-cap"></i> Education & Training</li>
              <li><i className="fas fa-truck"></i> Logistics & Supply Chain</li>
              <li><i className="fas fa-hotel"></i> Hospitality & Tourism</li>
              <li><i className="fas fa-leaf"></i> NGOs & Non-Profits</li>
              <li><i className="fas fa-building"></i> Real Estate & Construction</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Why Choose HRCONSOL for HR Policies?</h2>
            <p className="track-record-intro"><strong>Trusted by Leading Organizations:</strong></p>
            <ul className="benefits-list">
              <li>
                <span className="benefit-heading">300+ Policy Documents Created</span>
                <span className="benefit-subheading">for businesses across diverse industries</span>
              </li>
              <li>
                <span className="benefit-heading">100% Legal Compliance</span>
                <span className="benefit-subheading">aligned with Pakistan labor laws and best practices</span>
              </li>
              <li>
                <span className="benefit-heading">Custom Solutions</span>
                <span className="benefit-subheading">tailored to your industry, size, and culture</span>
              </li>
              <li>
                <span className="benefit-heading">Easy Implementation</span>
                <span className="benefit-subheading">with templates, training, and communication support</span>
              </li>
              <li>
                <span className="benefit-heading">Expert Consultants</span>
                <span className="benefit-subheading">with 15+ years of HR and legal expertise</span>
              </li>
              <li>
                <span className="benefit-heading">Ongoing Updates</span>
                <span className="benefit-subheading">policy revisions as laws and regulations change</span>
              </li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Our Process</h2>
            <div className="process-steps">
              <div className="step">
                <h3>Step 1: Assessment</h3>
                <p>Understanding your current policies, gaps, and requirements.</p>
              </div>
              <div className="step">
                <h3>Step 2: Policy Design</h3>
                <p>Drafting comprehensive, compliant, and customized policies.</p>
              </div>
              <div className="step">
                <h3>Step 3: Review & Approval</h3>
                <p>Collaborating with stakeholders for feedback and refinement.</p>
              </div>
              <div className="step">
                <h3>Step 4: Documentation</h3>
                <p>Creating professional employee handbooks and policy manuals.</p>
              </div>
              <div className="step">
                <h3>Step 5: Rollout & Training</h3>
                <p>Communicating policies to employees and training managers.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Packages Details</h2>
            
            <div className="process-steps">
              <div className="step package-card">
                <h3>1. STARTER</h3>
                <p>Small Businesses (Up to 50 Employees)</p>
                <ul className="package-list">
                  <li>Fee: PKR 75,000 (one-time)</li>
                  <li>5-7 core HR policies</li>
                  <li>Employee handbook (basic)</li>
                  <li>Digital policy templates</li>
                  <li>1 revision round</li>
                </ul>
                <p className="package-note">Perfect for startups establishing foundational HR policies.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>2. PROFESSIONAL</h3>
                <p>Growing Companies (51-200 Employees)</p>
                <ul className="package-list">
                  <li>Fee: PKR 175,000 (one-time)</li>
                  <li>10-12 comprehensive HR policies</li>
                  <li>Professional employee handbook</li>
                  <li>Manager training session</li>
                  <li>2 revision rounds</li>
                  <li>Policy communication support</li>
                </ul>
                <p className="package-note">Ideal for businesses seeking comprehensive policy frameworks.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>3. ENTERPRISE</h3>
                <p>Large Organizations (200+ Employees)</p>
                <ul className="package-list">
                  <li>Fee: PKR 350,000+ (based on scope)</li>
                  <li>15+ detailed HR policies</li>
                  <li>Complete policy manual & handbook</li>
                  <li>Multi-location customization</li>
                  <li>Training for HR team & managers</li>
                  <li>Annual policy review & updates</li>
                  <li>Legal compliance audit</li>
                </ul>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>
            </div>
          </div>

          <div className="service-cta">
            <h2>Need Professional HR Policies?</h2>
            <p>Let HRCONSOL create compliant, comprehensive policies for your organization.</p>
            <button onClick={() => navigate('/#contact')} className="cta-button">Contact Us</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRPolicy;
